import { Injectable, NgZone } from '@angular/core';
import { defer, interval, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { zendeskLog, ZendeskLogLevel } from '../zendesk-logger';
import { LocalStorageService } from '../local-storage.service';
import { ChatAdapter } from './chat/chat.adapter';
import { MessengerChatAdapter } from './chat/messenger-chat.adapter';

/**
 * Modo do widget de atendimento Zendesk ativo nesta sessão de página.
 *
 * - `messenger` → Web Widget for messaging (Zendesk Messaging).
 * - `off`       → nenhum widget: Messenger desabilitado ou sem key no ambiente,
 *                 ou falha ao resolver o modo.
 */
export type ZendeskMode = 'messenger' | 'off';

/**
 * Override de modo para QA, apenas fora de produção (ver `readOverride`).
 * Ex.: `localStorage.setItem('zendesk_mode_override', 'messenger')`.
 */
const MODE_OVERRIDE_KEY = 'zendesk_mode_override';

/** Chave do token de sessão; o Messenger só sobe para cliente autenticado. */
const AUTH_TOKEN_KEY = 'ls.token';

/** Intervalo de espera pelo login (não há Observable de sessão no AuthService legado). */
const AUTH_POLL_INTERVAL_MS = 2000;

/**
 * Única fonte de verdade sobre se esta página carrega o widget do Zendesk.
 *
 * O Messenger é o único widget de atendimento: a funcionalidade beta
 * `zendesk-messenger` (PF-4491) não é mais consultada e o Chat legado (Classic)
 * foi removido (PF-4657).
 *
 * O modo é resolvido UMA vez por carregamento de página e permanece estável: o
 * snippet do Zendesk é injetado uma vez e amarrado a uma key, e o SDK não
 * suporta trocar de key em runtime (spike PF-4439 no Jira). Mudanças de
 * configuração só valem no próximo carregamento.
 *
 * Se o Messenger não está disponível (desabilitado, sem key ou falha ao
 * resolver o modo), não há widget: a página fica sem chat.
 */
@Injectable({
  providedIn: 'root',
})
export class ZendeskModeService {
  private readonly modeSubject = new ReplaySubject<ZendeskMode>(1);

  /**
   * Modo resolvido para esta sessão de página. `ReplaySubject(1)`: emite uma
   * única vez e entrega o mesmo valor a quem se inscrever depois, garantindo
   * que o widget nunca seja reavaliado com a página carregada.
   */
  public readonly mode$: Observable<ZendeskMode> = this.modeSubject.asObservable();

  private resolvedMode: ZendeskMode = 'off';

  constructor(
    private localStorage: LocalStorageService,
    private ngZone: NgZone,
    private messengerAdapter: MessengerChatAdapter
  ) {
    // Fora da zona do Angular: a espera pelo login é um polling que rodaria
    // change detection global a cada 2s até alguém logar. A emissão final
    // volta para dentro da zona (ver `publish`), senão o `async` do template
    // do ZendeskWidgetComponent não renderizaria o widget.
    // `defer` leva ao `catchError` também os erros síncronos de `resolve()`
    // (ex.: localStorage indisponível), que de outro modo escapariam do construtor.
    this.ngZone.runOutsideAngular(() => {
      defer(() => this.resolve())
        .pipe(
          take(1),
          // Sem widget, e não Messenger: o que lança aqui é a leitura do
          // localStorage, da qual o ZendeskMessengerComponent também depende
          // (`ls.user`, `ls.token`) — o Messenger não subiria de qualquer forma.
          catchError((error) => {
            this.log(`Falha ao resolver o modo, nenhum widget será carregado. Erro: ${error}`, 'error');

            return of<ZendeskMode>('off');
          })
        )
        .subscribe((mode) => this.publish(mode));
    });
  }

  /**
   * Leitura síncrona do modo já resolvido, para quem é acionado por interação
   * do usuário (ChatService, FaqService) e não pode esperar por um Observable.
   * Retorna `off` enquanto a resolução não concluiu.
   */
  public get currentMode(): ZendeskMode {
    return this.resolvedMode;
  }

  /**
   * Adapter do Messenger quando o modo ativo é `messenger`, ou `null` quando não
   * há widget (`off`). Consumido pelo ChatService.
   */
  public get activeAdapter(): ChatAdapter | null {
    return this.currentMode === 'messenger' ? this.messengerAdapter : null;
  }

  /** Key do snippet do Messenger, ou `null` se ausente ou ainda placeholder. */
  public get messengerKey(): string | null {
    const key = this.config.messengerKey;

    return this.isConfigured(key) ? key : null;
  }

  /**
   * Ordem de decisão:
   *
   * 1. Override de QA (só fora de produção) — permite forçar `messenger` ou
   *    `off` em homologação.
   * 2. Chaves do ambiente: sem `messengerEnabled` ou sem key do Messenger, não
   *    há widget (`off`).
   * 3. Messenger, assim que houver sessão autenticada.
   */
  private resolve(): Observable<ZendeskMode> {
    const override = this.readOverride();

    if (override) {
      this.log(`Override de modo ativo (não-produção): ${override}`, 'warn');

      // Aviso explícito para o QA: o override força o Messenger, mas se a key
      // não estiver configurada o widget simplesmente não carrega. Sem esta
      // mensagem, o sintoma seria "forcei messenger e não apareceu nada".
      if (override === 'messenger' && !this.messengerKey) {
        this.log(
          'Override pediu "messenger" mas a key do Messenger não está configurada — ' +
            'o widget NÃO vai carregar. Configure a messengerKey no environment.',
          'warn'
        );
      }

      return of(override);
    }

    const config = this.config;

    if (!config.messengerEnabled) {
      this.log('Messenger desabilitado no ambiente — nenhum widget será carregado.', 'warn');

      return of<ZendeskMode>('off');
    }

    if (!this.isConfigured(config.messengerKey)) {
      this.log(
        'Messenger habilitado no ambiente mas sem key configurada — nenhum widget será carregado.',
        'warn'
      );

      return of<ZendeskMode>('off');
    }

    return this.waitForAuthenticatedSession().pipe(map(() => 'messenger' as ZendeskMode));
  }

  /**
   * Aguarda a existência de um token de sessão. O Messenger autentica o cliente
   * logado (JWT por cliente), então só sobe com sessão — e o login não recarrega
   * a página, por isso o polling (o AuthService legado não expõe Observable de sessão).
   *
   * Sem limite de tentativas de propósito: o usuário pode ficar na tela de
   * login por tempo indeterminado, e sem widget nenhum até logar.
   */
  private waitForAuthenticatedSession(): Observable<void> {
    if (this.hasSession()) {
      return of(null);
    }

    this.log('Aguardando login para resolver o modo do Zendesk...');

    return interval(AUTH_POLL_INTERVAL_MS).pipe(
      filter(() => this.hasSession()),
      take(1),
      map(() => null)
    );
  }

  private hasSession(): boolean {
    return !!this.localStorage.get(AUTH_TOKEN_KEY);
  }

  /**
   * Publica o modo resolvido. `resolvedMode` é atualizado antes da emissão
   * para que `currentMode` já esteja correto para qualquer consumidor síncrono.
   */
  private publish(mode: ZendeskMode): void {
    this.resolvedMode = mode;
    this.log(`Modo resolvido para esta sessão: ${mode}`);
    this.ngZone.run(() => this.modeSubject.next(mode));
  }

  /**
   * Override de modo por localStorage, para QA forçar `messenger` ou `off` em
   * homologação. Ignorado em produção — lá o modo vem exclusivamente do
   * ambiente.
   */
  private readOverride(): ZendeskMode | null {
    if (environment.production) {
      return null;
    }

    const valor = this.localStorage.get(MODE_OVERRIDE_KEY);

    return valor === 'messenger' || valor === 'off' ? valor : null;
  }

  private get config(): { messengerEnabled?: boolean; messengerKey?: string } {
    return environment.zendesk || {};
  }

  /**
   * Uma key só é utilizável se existir e já tiver sido substituída no build —
   * os placeholders (`7e075a4a-0541-4e12-b7d7-0f01660d802d`) chegam intactos quando se
   * roda o projeto localmente sem o `sed` do pipeline.
   */
  private isConfigured(key: string): boolean {
    return !!key && key.indexOf('__') !== 0;
  }

  private log(message: string, level: ZendeskLogLevel = 'log'): void {
    zendeskLog('ZendeskMode', message, level);
  }
}
